/**
 * GMA-1617 (CompassCare Web 2/14 — Overview) — quick actions bar.
 *
 * The Overview's primary actions (per the AC): Notify Caregivers, Create Alert,
 * Dispatch Team, Generate SITRep. Rendered as a labelled row of buttons; each
 * fires its injected handler. An action with no handler renders disabled (not a
 * focusable-but-inert control), so the bar degrades gracefully before every
 * flow is wired. Colors from the single design-token set.
 */
import React from "react";
import PropTypes from "prop-types";
import { theme } from "../../styles/designTokens";

// Canonical order + labels (the AC fixes these four).
export const QUICK_ACTIONS = Object.freeze(
  [
    { key: "notifyCaregivers", label: "Notify Caregivers" },
    { key: "createAlert", label: "Create Alert" },
    { key: "dispatchTeam", label: "Dispatch Team" },
    { key: "generateSitrep", label: "Generate SITRep" },
  ].map(Object.freeze), // freeze the entries too, not just the array
);

export default function QuickActions({
  onNotifyCaregivers,
  onCreateAlert,
  onDispatchTeam,
  onGenerateSitrep,
}) {
  const handlers = {
    notifyCaregivers: onNotifyCaregivers,
    createAlert: onCreateAlert,
    dispatchTeam: onDispatchTeam,
    generateSitrep: onGenerateSitrep,
  };

  return (
    <div
      data-testid="quick-actions"
      role="group"
      aria-label="Quick actions"
      style={{ display: "flex", flexWrap: "wrap", gap: 8, fontFamily: theme.fontUI }}
    >
      {QUICK_ACTIONS.map(({ key, label }) => {
        const handler = handlers[key];
        const enabled = typeof handler === "function";
        return (
          <button
            key={key}
            type="button"
            data-testid={`quick-action-${key}`}
            disabled={!enabled}
            onClick={enabled ? handler : undefined}
            style={{
              minHeight: 44, // 44px touch-AA hit target (Sidebar uses 44; KpiTile 64 — both meet AA)
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${theme.border}`,
              background: theme.surface,
              color: theme.textPrimary,
              cursor: enabled ? "pointer" : "default",
              opacity: enabled ? 1 : 0.5,
              fontFamily: theme.fontUI,
              fontSize: 13,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

QuickActions.propTypes = {
  /** Notify the client's caregivers. */
  onNotifyCaregivers: PropTypes.func,
  /** Manually create an alert. */
  onCreateAlert: PropTypes.func,
  /** Dispatch a response team. */
  onDispatchTeam: PropTypes.func,
  /** Generate a situation report (BLUF). */
  onGenerateSitrep: PropTypes.func,
};
